import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Compass, UploadSimple, MagnifyingGlass, ArrowRight, Warning } from "@phosphor-icons/react";

const CONF_COLOR = { high: "text-[var(--success)]", medium: "text-amber-500", low: "text-[var(--alert)]" };

export const FieldPackage = ({ clientId, onUseBasis, onGenerateBasis, onGeneratePreset }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(null);
  const fileRef = useRef(null);

  const run = async () => {
    if (!text.trim() && !file) { toast.error("Paste field notes / documents or attach a file."); return; }
    setBusy(true); setRec(null);
    try {
      const r = await api.fieldAnalysis({ text, clientId, file });
      setRec(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed.");
    } finally { setBusy(false); }
  };

  const useIt = () => {
    const note = rec?.mp_note
      || `Basis of Bearing: ${rec?.record_bearing} from ${rec?.from_monument} to ${rec?.to_monument}.`;
    onUseBasis?.(note);
    setOpen(false);
  };

  const basisNote = () =>
    rec?.mp_note || `Basis of Bearing: ${rec?.record_bearing} from ${rec?.from_monument} to ${rec?.to_monument}.`;

  const generateNow = () => {
    onGenerateBasis?.(basisNote());
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setRec(null); } }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="field-package-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--primary)] hover:bg-[var(--primary)]/10">
          <Compass size={14} weight="bold" /> Field Analysis
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px] max-h-[88vh] overflow-y-auto" data-testid="field-package-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Compass size={18} weight="bold" className="text-[var(--primary)]" />Field Package Analysis</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">
            Paste field notes, a legal description, deed calls or prior-survey text (or attach a file). LispForge reads it and <b>recommends a Basis of Bearing</b> for the MP&nbsp;&rarr;&nbsp;TP routine.
          </DialogDescription>
        </DialogHeader>

        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="field-package-text"
          placeholder="e.g.  Beginning at a found 5/8&quot; rebar at the NE corner; thence N45-30-00E 150.00' to a found iron pipe…"
          className="min-h-[130px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />

        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" className="hidden" data-testid="field-package-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            accept=".txt,.csv,.pdf,.doc,.docx,.lsp,.dxf,.dwg" />
          <button data-testid="field-package-attach" onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 text-[11px] font-mono text-[var(--text-dim)] hover:text-[var(--primary)]">
            <UploadSimple size={13} /> {file ? file.name : "Attach a document"}
          </button>
          {file && <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-[11px] text-[var(--alert)] font-mono">remove</button>}
          <Button size="sm" onClick={run} disabled={busy} data-testid="field-package-analyze"
            className="h-8 rounded-[3px] text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white ml-auto gap-1.5">
            <MagnifyingGlass size={13} weight="bold" /> {busy ? "Analyzing…" : "Recommend basis"}
          </Button>
        </div>

        {rec && (
          <div className="border border-[var(--primary)]/40 rounded-[3px] p-3 flex flex-col gap-2.5" data-testid="field-package-result">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--primary)]">Recommended Basis of Bearing</span>
              <span className={`text-[10px] font-mono uppercase ${CONF_COLOR[rec.confidence] || "text-[var(--text-dim)]"}`} data-testid="field-package-confidence">
                {rec.confidence} confidence
              </span>
            </div>
            <div className="font-mono text-sm text-[var(--text)]" data-testid="field-package-bearing">
              {rec.record_bearing || "—"}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
              <div><span className="text-[var(--text-dim)]">From: </span>{rec.from_monument || "—"}</div>
              <div><span className="text-[var(--text-dim)]">To: </span>{rec.to_monument || "—"}</div>
            </div>
            {rec.rationale && <div className="text-[11px] text-[var(--text-dim)] leading-snug">{rec.rationale}</div>}

            {rec.alternatives?.length > 0 && (
              <div className="border-t border-[var(--border)] pt-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] mb-1">Alternatives</div>
                {rec.alternatives.map((a, i) => (
                  <div key={i} className="text-[11px] font-mono text-[var(--text-dim)] leading-snug" data-testid={`field-package-alt-${i}`}>
                    <span className="text-[var(--text)]">{a.bearing}</span> · {a.pair}{a.note ? ` — ${a.note}` : ""}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-start gap-1.5 text-[10px] text-amber-500/90 leading-snug">
              <Warning size={13} className="shrink-0 mt-0.5" /> {rec.disclaimer}
            </div>

            <div className="flex items-center gap-2 self-start flex-wrap">
              <Button size="sm" onClick={generateNow} disabled={!clientId} data-testid="field-package-generate"
                className="h-8 rounded-[3px] text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white gap-1.5">
                <ArrowRight size={13} weight="bold" /> Generate MP routine now
              </Button>
              <Button size="sm" variant="ghost" onClick={useIt} data-testid="field-package-use"
                className="h-8 rounded-[3px] text-xs gap-1.5 border border-[var(--border)]">
                Just prefill the prompt
              </Button>
            </div>
            {onGeneratePreset && (
              <div className="flex items-center gap-2 flex-wrap border-t border-[var(--border)] pt-2 mt-0.5" data-testid="field-package-next">
                <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">Or jump to:</span>
                <button data-testid="field-package-gen-contours" disabled={!clientId}
                  onClick={() => { onGeneratePreset("contours"); setOpen(false); }}
                  className="h-7 px-2 rounded-[3px] border border-[var(--border)] text-[11px] font-mono text-[var(--text)] hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:opacity-40">
                  Generate Contours
                </button>
                <button data-testid="field-package-gen-grading" disabled={!clientId}
                  onClick={() => { onGeneratePreset("grading"); setOpen(false); }}
                  className="h-7 px-2 rounded-[3px] border border-[var(--border)] text-[11px] font-mono text-[var(--text)] hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:opacity-40">
                  Generate Grading
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
